import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { Header } from '@photosi/rn-components/headers/header/Header';
import { observer } from 'mobx-react-lite';
import { GestureResponderEvent, View } from 'react-native';
import { useStore } from 'src/contexts/StoreContext';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { IUserImageAreaModel } from '../../../../../store/_models/userImageArea';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { headerEditorTestID } from 'src/helpers/testIDs.helper';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import { styles } from './HeaderEditorDesktop.styles';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { BUTTON_TEXT_POSITIONS } from '@photosi/rn-components/buttons/button/Button.types.constants';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { parseAnalyticsParamsByPreview } from 'src/_shared/analytics.utils';

export const HeaderEditorDesktop = observer(
  ({ onBack, onSave }: { onBack: () => Promise<void>; onSave: () => Promise<void> }) => {
    const {
      i18n,
      project,
      dynamicModal: { closeModal, openModal },
    } = useStore();

    const handleDiscard = (event: GestureResponderEvent) => {
      closeModal();
      onBack();
      event.preventDefault();
      event.stopPropagation();
      project.goBack();
    };

    const handleBack = (event: GestureResponderEvent) => {
      const anyUnsavedChanges = project.activePreview?.userImageAreas.some(
        (userImageArea: IUserImageAreaModel) => userImageArea.userImage && userImageArea.userImage.unsavedChanges,
      );
      amplitude.trackEvent(AnalyticsEvents.personalize.backToProductClick);
      if (anyUnsavedChanges) {
        openModal({
          dynamicModalView: DYNAMIC_MODAL_VIEWS.SaveChanges,
          onPressSaveChanges: onSave,
          onPressDiscardChanges: () => {
            handleDiscard(event);
          },
          afterInit: () =>
            amplitude.trackEvent(
              AnalyticsEvents.modifyPhoto.unsavedChangesView,
              parseAnalyticsParamsByPreview(project.activePreview),
            ),
        });
      } else {
        handleDiscard(event);
      }
    };

    const LeftElement = () => (
      <View style={styles.left_element_container}>
        <TextIconButton
          style={styles.backButton}
          innerStyle={styles.backButtonInner}
          focusStyle={styles.backButtonFocus}
          onPress={async event => {
            handleBack(event);
          }}
          testID={headerEditorTestID(TestIDs.BACK)}
          accessible={true}
          accessibilityLabel={i18n.t('edit:back')}
          accessibilityRole="button"
          focusId={headerEditorTestID(TestIDs.BACK)}
          text={i18n.t('edit:back')}
          textStyle={styles.backButtonText}
          focusOrder={0}
          focusGroup={FocusGroups.HEADER}
          textPosition={BUTTON_TEXT_POSITIONS.RIGHT}
          iconName={ICON_NAMES.ArrowLeft1}
          iconStyle={styles.backButtonIcon}
          size={TEXT_ICON_BUTTON_SIZES.LARGE}
          type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
        />
      </View>
    );

    const CenterElement = () => (
      <TextLato style={styles.title} testID={headerEditorTestID(TestIDs.TITLE)}>
        {i18n.t('edit:edit-photo')}
      </TextLato>
    );

    const RightElement = () => <></>;

    return <Header leftElement={<LeftElement />} centerElement={<CenterElement />} rightElement={<RightElement />} />;
  },
);
